import { Heart, FileText, Vote, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/community/client";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { SectionEyebrow } from "@/components/SectionEyebrow";

const memberBenefits = [
  {
    icon: Vote,
    title: "Community input",
    description: "Help us decide our next story.",
  },
  {
    icon: FileText,
    title: "Quarterly behind-the-scenes updates",
    description: "See how your contribution supports our ground-up initiatives.",
  },
];

const SHORT_DISCLAIMER =
  "Voluntary contribution - not a tax-deductible donation. EcoCupid is a Singapore-registered for-profit social enterprise.";

interface ImpactCircleSectionProps {
  variant?: "hero" | "standalone" | "compact";
}

export const ImpactCircleSection = ({ variant = "standalone" }: ImpactCircleSectionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const handleJoinCircle = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-impact-circle-checkout");
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === "compact") {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
              <Heart className="w-5 h-5 text-black" />
            </div>
            <div>
              <h3 className="font-display font-bold text-lg text-foreground">
                Support EcoCupid ASEAN
              </h3>
              <p className="text-sm text-primary font-semibold">$10/month - voluntary</p>
            </div>
          </div>
          <p className="text-sm text-black/70 mb-4">
            Help fund our ground-up operations across the 11 ASEAN nations. Contributors receive community perks as a thank-you.
          </p>
          <Button onClick={handleJoinCircle} disabled={isLoading} className="w-full">
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
            Support EcoCupid ASEAN
          </Button>
          <div className="mt-3 flex justify-center">
            <iframe width="380" height="38" style={{ border: 0 }} src="https://climate.stripe.com/badge/Or0A8v?theme=dark&size=small&locale=en-US" title="Stripe Climate badge" />
          </div>
          <p className="text-[11px] italic text-black/60 mt-3 leading-snug">{SHORT_DISCLAIMER}</p>
        </CardContent>
      </Card>
    );
  }

  if (variant === "hero") {
    return (
      <div className="mt-12 p-6 rounded-card bg-gradient-to-br from-primary/10 via-secondary/10 to-accent border border-primary/20 animate-in fade-in delay-500">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
            <Heart className="w-6 h-6 text-black" />
          </div>
          <div>
            <h2 className="font-display font-bold text-xl text-foreground">Support EcoCupid ASEAN</h2>
            <p className="text-sm text-primary font-semibold">$10/month - voluntary contribution</p>
          </div>
        </div>
        <p className="text-black/75 text-sm mb-4 max-w-md">
          Voluntary community support for our ground-up operations across the 11 ASEAN nations - from on-the-ground project verification to creator production costs.
        </p>
        <Button onClick={handleJoinCircle} disabled={isLoading} size="lg">
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
          Support EcoCupid ASEAN
        </Button>
        <div className="mt-3">
          <iframe width="380" height="38" style={{ border: 0 }} src="https://climate.stripe.com/badge/Or0A8v?theme=dark&size=small&locale=en-US" title="Stripe Climate badge" />
        </div>
        <p className="text-xs italic text-black/65 mt-3 max-w-md leading-snug">{SHORT_DISCLAIMER}</p>
      </div>
    );
  }

  // Standalone - deep forest knockout band per design system
  return (
    <section className="py-14 md:py-20 bg-primary">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <SectionEyebrow
            eyebrow="Support"
            heading="Back the work."
            dek="Your contribution funds EcoCupid's mission to feature Southeast Asia's eco-projects through unique and professional storytelling."
            tone="dark"
          />

          <p className="font-display font-extrabold uppercase tracking-[0.16em] text-secondary text-xl md:text-2xl text-center mb-8">
            USD 10 / Month
          </p>

          <div className="max-w-2xl mx-auto mb-8">
            <p className="font-display font-bold uppercase tracking-[0.18em] text-secondary text-xs text-center mb-4">
              Perks
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {memberBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className="rounded-card bg-[#f2ecd8] p-5 border border-black/5"
                >
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center mb-3">
                    <benefit.icon className="w-5 h-5 text-black" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-display font-bold text-foreground text-lg mb-1">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-black/70">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={handleJoinCircle}
              disabled={isLoading}
              size="lg"
              variant="lime"
              className="px-10"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
              Support EcoCupid - $10/month
            </Button>
            <div className="mt-4 flex flex-col items-center gap-1">
              <iframe width="380" height="38" style={{ border: 0 }} src="https://climate.stripe.com/badge/Or0A8v?theme=dark&size=small&locale=en-US" title="Stripe Climate badge" />
              
            </div>
            <p className="text-xs italic text-white/70 mt-4 max-w-2xl mx-auto leading-relaxed">
              Voluntary contribution - not a tax-deductible donation. EcoCupid is a Singapore-registered for-profit social enterprise.
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};
